import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';

import { errorMessage, normalizeApiError } from '../services/apiError';
import { chatService } from '../services/chatService';
import type { ChatMessage } from '../services/chatService';
import { eventService } from '../services/eventService';
import { trackFirstMessage } from '../utils/analytics';
import { quotaWaitSentence } from '../utils/quotaQueue';

const STORAGE_KEY = 'landing_text_input';

interface SendMessageResult {
  success: boolean;
  message?: ChatMessage;
  error?: string;
}

const loadSavedInput = (): string => {
  const savedText = localStorage.getItem(STORAGE_KEY);

  return savedText || '';
};

const saveInputToStorage = (value: string): void => {
  const trimmed = value.trim();

  if (trimmed) {
    localStorage.setItem(STORAGE_KEY, value);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
};

const clearSavedInput = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

const insufficientCreditsResult = (): SendMessageResult => {
  eventService.emit('insufficient_credits', {});

  return { success: false, error: 'INSUFFICIENT_CREDITS' };
};
/** What a first message that could not be sent means for the landing page. */
const failedSendResult = (error: unknown): SendMessageResult => {
  const normalized = normalizeApiError(error);

  if (normalized.status === 402) return insufficientCreditsResult();
  // Over the plan's hourly chat quota: the text stays in the box, and the plan notice offers the upgrade.
  if (normalized.quotaQueue) {
    return { success: false, error: quotaWaitSentence({ ...normalized.quotaQueue, subject: 'Your message' }) };
  }
  console.error('Failed to send message:', error);

  return {
    success: false,
    error: errorMessage(normalized, 'We couldn’t send your message. Please try again.'),
  };
};

const sendChatMessage = async (text: string, mediaUrls?: string[], isPublic?: boolean): Promise<SendMessageResult> => {
  try {
    const message = await chatService.sendMessage(text, 'user', undefined, mediaUrls, isPublic);

    return { success: true, message };
  } catch (error) {
    return failedSendResult(error);
  }
};

const handleSubmitSuccess = (clearInput: () => void): void => {
  clearSavedInput();
  clearInput();
  trackFirstMessage();
};

const resetSubmitState = (
  setIsSubmitting: (val: boolean) => void,
  setSubmitError: (val: string | null) => void
): void => {
  setIsSubmitting(true);
  setSubmitError(null);
};

const handleSubmitResult = (
  result: SendMessageResult,
  setSubmitError: (val: string | null) => void
): ChatMessage | null => {
  if (!result.success || !result.message?.project_id) {
    const msg = result.error === 'INSUFFICIENT_CREDITS'
      ? 'Not enough credits. Please top up or upgrade your plan.'
      : result.error || 'We couldn’t send your message. Check your connection and try again.';

    setSubmitError(msg);

    return null;
  }

  return result.message;
};

const createSubmitFunction = (
  setIsSubmitting: (val: boolean) => void,
  setSubmitError: (val: string | null) => void
) => async (text: string, mediaUrls?: string[], isPublic?: boolean) => {
  resetSubmitState(setIsSubmitting, setSubmitError);

  const result = await sendChatMessage(text, mediaUrls, isPublic);

  setIsSubmitting(false);

  return handleSubmitResult(result, setSubmitError);
};

const useSubmitState = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  return { isSubmitting, setIsSubmitting, submitError, setSubmitError };
};

export const useMessageSubmit = () => {
  const state = useSubmitState();
  const navigate = useNavigate();
  const { setIsSubmitting, setSubmitError } = state;

  // Stable identities: these are threaded into the landing page's send handler,
  // which in turn is a prop on the chat input. Rebuilding them on every render
  // would defeat memoisation of everything downstream.
  const submit = useMemo(
    () => createSubmitFunction(setIsSubmitting, setSubmitError),
    [setIsSubmitting, setSubmitError],
  );
  const onSuccess = useCallback(
    (clearInput: () => void) => handleSubmitSuccess(clearInput),
    [],
  );

  return {
    isSubmitting: state.isSubmitting,
    submitError: state.submitError,
    submit,
    onSuccess,
    navigate,
  };
};

const handleAuthenticatedSend = async (
  text: string,
  mediaUrls: string[] | undefined,
  submit: (text: string, mediaUrls?: string[], isPublic?: boolean) => Promise<ChatMessage | null>,
  onSuccess: (clearInput: () => void) => void,
  clearInput: () => void,
  navigate: NavigateFunction,
  isPublic?: boolean,
): Promise<boolean> => {
  const message = await submit(text, mediaUrls, isPublic);
  const projectId = message?.project_id;

  if (projectId) {
    onSuccess(clearInput);
    const sessionPath = message.session_id ? `/${encodeURIComponent(message.session_id)}` : '';

    navigate(`/planning/${projectId}${sessionPath}`, {
      state: {
        landingBootstrap: {
          message,
          initialProjectBootstrap: true,
        },
      },
    });

  }

  return Boolean(projectId);
};

export const handleSendMessage = async (
  isAuthenticated: boolean,
  text: string,
  mediaUrls: string[] | undefined,
  openModal: () => void,
  submit: (text: string, mediaUrls?: string[], isPublic?: boolean) => Promise<ChatMessage | null>,
  onSuccess: (clearInput: () => void) => void,
  clearInput: () => void,
  navigate: NavigateFunction,
  isPublic?: boolean,
): Promise<boolean> => {
  if (!isAuthenticated) {
    openModal();

    return false;
  }

  return handleAuthenticatedSend(text, mediaUrls, submit, onSuccess, clearInput, navigate, isPublic);
};

export const useInputValue = () => {
  const [inputValue, setInputValue] = useState(() => loadSavedInput());

  const handleChange = useCallback((value: string) => {
    setInputValue(value);
    saveInputToStorage(value);
  }, []);

  return { inputValue, setInputValue, handleChange };
};
